"use client";

import Close16 from "@ui/icons/16x16/close-16.svg";
import { cn } from "@ui/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "./Button";

export function FloatingCallout(props: { children: React.ReactNode; className?: string }) {
  const [isHidden, setIsHidden] = useState(false);
  const [isVisible, setIsVisible] = useState(false); // Start visible
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      // When footer is intersecting (visible), hide the callout
      setIsVisible(!entry.isIntersecting);
    });
  }, []);

  useEffect(() => {
    // Create intersection observer with stable callback
    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold: 0,
      rootMargin: "0px 0px -100px 0px", // Start hiding when footer is 100px from viewport bottom
    });

    // Find footer by className (more reliable than tag selector)
    const footer = document.querySelector("footer");

    if (footer && observerRef.current) {
      observerRef.current.observe(footer);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleIntersection]);

  if (isHidden) return null;

  return (
    <div
      className={cn(
        "fixed bottom-32 left-16 z-50 hidden rounded-xl bg-default shadow-md transition-all duration-300 ease-in-out md:inline-block",
        isVisible ? "translate-x-0 opacity-100 delay-1000" : "-translate-x-full opacity-0",
        props.className,
      )}
    >
      <Button
        className='absolute right-4 top-4 -translate-y-1/2 translate-x-1/2 border border-default shadow-xs'
        onClick={() => setIsHidden(!isHidden)}
        size='pill'
        variant='elevated'
      >
        <Close16 />
      </Button>
      {props.children}
    </div>
  );
}
